import { createContext, type PropsWithChildren, useContext } from "react";
import type { CapabilityContext, CapabilityKey } from "./capabilities";
import { getFeatureGate } from "./registry";
import type { FeatureGate } from "./types";

const FeatureGateContext = createContext<FeatureGate | null>(null);

export type FeatureGateProviderProps = PropsWithChildren<{
  gate: FeatureGate;
}>;

export const FeatureGateProvider = ({ gate, children }: FeatureGateProviderProps) => (
  <FeatureGateContext.Provider value={gate}>{children}</FeatureGateContext.Provider>
);

export const useFeatureGateInstance = (): FeatureGate => {
  const gate = useContext(FeatureGateContext);
  return gate ?? getFeatureGate();
};

export const useFeatureGate = (capability: CapabilityKey, context?: CapabilityContext) =>
  useFeatureGateInstance().has(capability, context);

export type FeatureGateGuardProps = PropsWithChildren<{
  capability: CapabilityKey;
  context?: CapabilityContext;
  fallback?: React.ReactNode;
}>;

export const FeatureGateGuard = ({
  capability,
  context,
  fallback = null,
  children,
}: FeatureGateGuardProps) =>
  useFeatureGate(capability, context) ? <>{children}</> : <>{fallback}</>;
